import { useCallback, useMemo, useState } from "react";
import {
  Box,
  Backdrop,
  Card,
  CardContent,
  Dialog,
  Typography,
  IconButton,
  Button,
  Stepper,
  Step,
  StepLabel,
  Chip,
  Divider,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
} from "@mui/material";
import { makeStyles, styled } from "@mui/styles";
import * as Yup from "yup";
import { Close, Add, Delete, Edit, CheckCircle } from "@mui/icons-material";
import { useSnackbar } from "notistack";
import { useDispatch } from "react-redux";
import PropTypes from "prop-types";
import dayjs from "dayjs";

import axios from "../utils/axios";
import { earliestActivityDate, activityHistoryDateMessage } from "../utils/activityHistory";
import IndividualActivityForm from "./IndividualActivityForm";
import { setProfile } from "../reducers/profile";
import { inputFieldValidator, descriptionValidator, buildDescriptionFields, resolveDescription } from "../utils/activityForm";

const cardPadding = {};

const useStyles = makeStyles((theme) => ({
  cardContent: {
    alignItems: "center",
    display: "flex",
    flexDirection: "column",
  },
  activityList: {
    width: "100%",
    backgroundColor: theme?.palette?.background?.paper || "#fff",
    borderRadius: 8,
    border: "1px solid #e0e0e0",
  },
  activityItem: {
    borderBottom: "1px solid #e0e0e0",
    "&:last-child": {
      borderBottom: "none",
    },
  },
  stepContent: {
    width: "100%",
    minHeight: 400,
  },
  reviewCard: {
    padding: 16,
    marginBottom: 12,
    backgroundColor: "#f9f9f9",
    borderRadius: 8,
    border: "1px solid #e0e0e0",
  },
}));

export default function BulkLogActivities({ open, onClose }) {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();

  const [activities, setActivities] = useState([]);
  const [currentStep, setCurrentStep] = useState(0); // 0 = list, 1 = add/edit form, 2 = review
  const [editingIndex, setEditingIndex] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const initialFormValues = useMemo(
    () => ({
      title: "",
      goal: null,
      activity_type: null,
      what_did_you_do: "",
      what_did_you_learn: "",
      ai_summary: "",
      ai_summary_tagged: false,
      location: null,
      address: "",
      sessions: [{ date: null, hours: "" }],
      skills: [],
    }),
    []
  );

  const LogActivitySchema = useCallback(
    () =>
      Yup.object().shape({
        title: Yup.string().min(1, "Too Short!").max(50, "Too Long!").required("Required"),
        goal: Yup.object()
          .required("Please choose a goal")
          .shape({ id: Yup.number().required("Please choose a goal") })
          .nullable(),
        activity_type: Yup.object().shape({ id: Yup.number() }).nullable(),
        what_did_you_do: inputFieldValidator(Yup),
        what_did_you_learn: inputFieldValidator(Yup),
        ai_summary: descriptionValidator(Yup),
        location: Yup.object()
          .required("Please choose a region")
          .shape({ id: Yup.number().required("Please choose a region") })
          .nullable(),
        sessions: Yup.array().of(
          Yup.object().shape({
            date: Yup.date().typeError("Please enter a date")
              .min(earliestActivityDate(), activityHistoryDateMessage())
              .max(dayjs(), "Session must be in the past")
              .required("Date is required"),
            hours: Yup.number()
              .required("Hours is required")
              .min(0.5, "Minimum 0.5 hours")
              .max(8, "Maximum 8 hours"),
          })
        ).min(1, "At least one session is required")
         .test("max-12-per-day", "", function (sessions) {
           if (!sessions) return true;
           const hoursPerDate = {};
           sessions.forEach((s) => {
             if (s.date && s.hours) {
               const key = dayjs(s.date).format("YYYY-MM-DD");
               hoursPerDate[key] = (hoursPerDate[key] || 0) + Number(s.hours);
             }
           });
           for (const [date, total] of Object.entries(hoursPerDate)) {
             if (total > 12) {
               return this.createError({ message: `Total hours on ${dayjs(date).format("DD MMM YYYY")} cannot exceed 12 (currently ${total})` });
             }
           }
           return true;
         }),
        skills: Yup.array().of(Yup.object().shape({ id: Yup.number() })),
      }),
    []
  );

  const handleAddActivity = useCallback(() => {
    setEditingIndex(null);
    setCurrentStep(1);
  }, []);

  const handleEditActivity = useCallback((index) => {
    setEditingIndex(index);
    setCurrentStep(1);
  }, []);

  const handleDeleteActivity = useCallback((index) => {
    setActivities((prev) => prev.filter((_, i) => i !== index));
    enqueueSnackbar("Activity removed from list", { variant: "info" });
  }, [enqueueSnackbar]);

  const handleFormSubmit = useCallback(
    (values) => {
      if (editingIndex !== null) {
        // Editing existing activity
        setActivities((prev) => {
          const updated = [...prev];
          updated[editingIndex] = values;
          return updated;
        });
        enqueueSnackbar("Activity updated", { variant: "success" });
      } else {
        // Adding new activity
        setActivities((prev) => [...prev, values]);
        enqueueSnackbar("Activity added to list", { variant: "success" });
      }
      setCurrentStep(0);
      setEditingIndex(null);
    },
    [editingIndex, enqueueSnackbar]
  );

  const handleGoToReview = useCallback(() => {
    if (activities.length === 0) {
      enqueueSnackbar("Please add at least one activity", { variant: "warning" });
      return;
    }
    setCurrentStep(2);
  }, [activities.length, enqueueSnackbar]);

  const handleBulkSubmit = useCallback(async () => {
    setIsSubmitting(true);
    try {
      const payload = {
        activities: activities.map((activity) => {
          const { sessions, title, skills, location, goal, address, activity_type, what_did_you_do, what_did_you_learn, beneficiary } = activity;

          const firstDate = sessions[0].date;
          const totalHours = sessions.reduce((sum, s) => sum + Number(s.hours || 0), 0);
          const startDateTime = dayjs(firstDate).hour(0).minute(0).second(0);
          const endDateTime = startDateTime.add(totalHours, "hours");

          return {
            mode: "individual",
            title,
            goal_id: goal?.id,
            location_id: location?.id,
            activity_type_id: activity_type?.id,
            what_did_you_do,
            what_did_you_learn,
            ...buildDescriptionFields(activity),
            beneficiary_id: beneficiary?.id || null,
            activity_date: dayjs(firstDate).format("YYYY-MM-DD"),
            starts: startDateTime.toISOString(),
            ends: endDateTime.toISOString(),
            skills: (skills || []).map((s) => ({ id: s.id })),
            address,
            sessions: sessions.map((s) => ({
              date: dayjs(s.date).format("YYYY-MM-DD"),
              hours: Number(s.hours),
            })),
          };
        }),
      };

      const response = await axios.post("activities/bulk", payload);
      const { success_count, failure_count } = response.data?.data || {};

      // Refresh profile to update completed_activity_ids, hours, and awards
      const profileResponse = await axios.get("auth/profile");
      dispatch(setProfile(profileResponse.data.data));

      if (failure_count > 0) {
        enqueueSnackbar(`${success_count} activities logged, ${failure_count} failed`, { variant: "warning" });
      } else {
        enqueueSnackbar(`${success_count} activities logged successfully!`, { variant: "success" });
      }
      onClose(true);
    } catch (error) {
      const data = error?.response?.data;
      // Prefer the server's message (now includes the specific reason). Otherwise dig
      // into the per-activity failures to surface the real validation reason (e.g.
      // "Session date must be within the last 6 months.") instead of a generic error.
      const nestedReason = (data?.data?.failed || data?.failed || [])
        .map((f) => f?.errors)
        .filter(Boolean)
        .flatMap((errs) => Object.values(errs).flat())[0];
      const errorMessage = data?.message || nestedReason || error?.message || "Failed to submit activities";
      enqueueSnackbar(errorMessage, { variant: "error" });
    } finally {
      setIsSubmitting(false);
    }
  }, [activities, enqueueSnackbar, onClose, dispatch]);

  const handleBack = useCallback(() => {
    if (currentStep === 1) {
      setEditingIndex(null);
    }
    setCurrentStep((prev) => Math.max(0, prev - 1));
  }, [currentStep]);

  const handleClose = useCallback(() => {
    if (activities.length > 0) {
      if (window.confirm("You have unsaved activities. Are you sure you want to close?")) {
        onClose(false);
      }
    } else {
      onClose(false);
    }
  }, [activities.length, onClose]);

  const getFormInitialValues = useMemo(() => {
    if (editingIndex !== null && activities[editingIndex]) {
      return activities[editingIndex];
    }
    return initialFormValues;
  }, [editingIndex, activities, initialFormValues]);

  const steps = ["Add Activities", "Review & Submit"];

  const renderActivityList = () => (
    <Box className={classes.stepContent}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">
          Activities to Log ({activities.length})
        </Typography>
        <Button variant="contained" startIcon={<Add />} onClick={handleAddActivity}>
          Add Activity
        </Button>
      </Box>

      {activities.length === 0 ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          No activities added yet. Click "Add Activity" to get started.
        </Alert>
      ) : (
        <List className={classes.activityList}>
          {activities.map((activity, index) => (
            <ListItem key={index} className={classes.activityItem}>
              <ListItemText
                primary={
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography fontWeight={600}>{activity.title}</Typography>
                    <Chip size="small" label={`${(activity.sessions || []).reduce((s, sess) => s + Number(sess.hours || 0), 0)}h`} color="primary" variant="outlined" />
                    <Chip size="small" label={`${(activity.sessions || []).length} session${(activity.sessions || []).length > 1 ? "s" : ""}`} variant="outlined" />
                  </Box>
                }
                secondary={
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      {dayjs((activity.sessions || [])[0]?.date).format("DD MMM YYYY")} | {activity.goal?.name || "No goal"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic", mt: 0.5 }}>
                      {resolveDescription(activity).substring(0, 100)}
                      {resolveDescription(activity).length > 100 ? "..." : ""}
                    </Typography>
                  </Box>
                }
              />
              <ListItemSecondaryAction>
                <IconButton edge="end" onClick={() => handleEditActivity(index)} sx={{ mr: 1 }}>
                  <Edit />
                </IconButton>
                <IconButton edge="end" onClick={() => handleDeleteActivity(index)} color="error">
                  <Delete />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      )}

      <Box mt={3} display="flex" justifyContent="flex-end">
        <Button
          variant="contained"
          color="primary"
          onClick={handleGoToReview}
          disabled={activities.length === 0}
        >
          Review & Submit ({activities.length})
        </Button>
      </Box>
    </Box>
  );

  const renderForm = () => (
    <Box className={classes.stepContent}>
      <Box display="flex" alignItems="center" mb={2}>
        <Button onClick={handleBack} sx={{ mr: 2 }}>
          &larr; Back to List
        </Button>
        <Typography variant="h6">
          {editingIndex !== null ? "Edit Activity" : "Add New Activity"}
        </Typography>
      </Box>
      <IndividualActivityForm
        initialValues={getFormInitialValues}
        LogActivitySchema={LogActivitySchema}
        handleSubmit={handleFormSubmit}
        submitButtonLabel={editingIndex !== null ? "UPDATE ACTIVITY" : "ADD TO LIST"}
      />
    </Box>
  );

  const renderReview = () => {
    const totalHours = activities.reduce((sum, a) => (a.sessions || []).reduce((s, sess) => s + Number(sess.hours || 0), sum), 0);

    return (
      <Box className={classes.stepContent}>
        <Box display="flex" alignItems="center" mb={2}>
          <Button onClick={handleBack} sx={{ mr: 2 }}>
            &larr; Back
          </Button>
          <Typography variant="h6">Review Activities</Typography>
        </Box>

        <Alert severity="info" sx={{ mb: 2 }}>
          <strong>{activities.length} activities</strong> totaling <strong>{totalHours} hours</strong> will be logged.
        </Alert>

        {activities.map((activity, index) => (
          <Box key={index} className={classes.reviewCard}>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start">
              <Box>
                <Typography variant="subtitle1" fontWeight={600}>
                  {index + 1}. {activity.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {dayjs((activity.sessions || [])[0]?.date).format("DD MMM YYYY")} | {(activity.sessions || []).reduce((s, sess) => s + Number(sess.hours || 0), 0)} hours | {(activity.sessions || []).length} session{(activity.sessions || []).length > 1 ? "s" : ""} | {activity.goal?.name}
                </Typography>
                <Typography variant="body2" sx={{ mt: 1, fontStyle: "italic" }}>
                  {resolveDescription(activity)}
                </Typography>
                {activity.skills?.length > 0 && (
                  <Box mt={1} display="flex" gap={0.5} flexWrap="wrap">
                    {activity.skills.map((skill) => (
                      <Chip key={skill.id} size="small" label={skill.name} variant="outlined" />
                    ))}
                  </Box>
                )}
              </Box>
              <Box display="flex" gap={1}>
                <IconButton size="small" onClick={() => handleEditActivity(index)}>
                  <Edit fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={() => handleDeleteActivity(index)} color="error">
                  <Delete fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          </Box>
        ))}

        <Divider sx={{ my: 3 }} />

        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Button onClick={() => setCurrentStep(0)}>
            Add More Activities
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={handleBulkSubmit}
            disabled={isSubmitting}
            startIcon={<CheckCircle />}
          >
            {isSubmitting ? "Submitting..." : `Log ${activities.length} Activities`}
          </Button>
        </Box>
      </Box>
    );
  };

  return (
    <Dialog
      BackdropComponent={styled(Backdrop, {
        name: "MuiModal",
        slot: "Backdrop",
        overridesResolver: (props, styles) => styles.backdrop,
      })({ zIndex: -1 })}
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      scroll="body"
      transitionDuration={{ appear: 0 }}
    >
      <Card sx={cardPadding}>
        <CardContent className={classes.cardContent}>
          <Box width="100%" display="flex" flexDirection="row" mb={1}>
            <Box flexGrow={1} />
            <Typography sx={{ fontSize: 28, fontWeight: 800 }}>Bulk Log Activities</Typography>
            <Box flexGrow={1} justifyContent="flex-end" display="flex">
              <IconButton onClick={handleClose}>
                <Close color="primary" />
              </IconButton>
            </Box>
          </Box>

          <Stepper activeStep={currentStep === 2 ? 1 : 0} sx={{ width: "100%", mb: 3 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {currentStep === 0 && renderActivityList()}
          {currentStep === 1 && renderForm()}
          {currentStep === 2 && renderReview()}
        </CardContent>
      </Card>
    </Dialog>
  );
}

BulkLogActivities.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};
